import { storage } from '../auth/utils';
import { VideoAudioConfig } from '../utils/video-and-audio-config';

const DefaultBandwidth = 2000000; // hope they have at least 2 MBps up
const BandwidthTextExpiresInMs = 10 * 60 * 1000; // 10 minutes
const OverallTimeoutInSec = 60; //1 minute
const bandwidthFactor = 0.6, // Only use ~60% of the available bandwidth
  videoToAudioBitrateScale = 8; // Starting at the minimum, for every 1 bit we add to audio, we add this many bits to video

let module = angular.module('bandwidth-test-client', []);

module.service('BandwidthTestClient', function (Requester, $q) {
  'ngInject';

  let audioBitrates = _(VideoAudioConfig.stream.audio_bitrates)
    .pluck('value')
    .map((x) => parseInt(x, 10))
    .filter()
    .value();
  let videoBitrates = _(VideoAudioConfig.stream.video_bitrates)
    .pluck('value')
    .map((x) => parseInt(x, 10))
    .filter((x) => x && x > 10000)
    .value();

  this.init = () => {
    return $q((resolve, reject) => {
      require.ensure([], () => {
        var BandwidthTestClient = require('bandwidth-test-client');
        resolve(BandwidthTestClient);
      });
    });
  };

  this.cancelIfInFlight = () => {
    if (this._bandwidthTest) {
      this._bandwidthTest.cancel();
    }
  };

  this.runTestOrUseRecentCached = (onProgress = null, alwaysRun = false) => {
    return $q((resolve, reject) => {
      this.init().then((BandwidthTest) => {
        // In order to prevent extra bandwidth probing, we can skip the bandwidth test if:
        //  - A bandwidth test has been done before, and
        //  - It was done from the same internet-facing address, and
        //  - It was done within the last 10 minutes.
        const lastBandwidthTest =
          storage.get('lastBwTest') && JSON.parse(storage.get('lastBwTest'));
        const skipBandwidthTest =
          lastBandwidthTest &&
          lastBandwidthTest.success &&
          (!Requester.address || lastBandwidthTest.requesterAddress == Requester.address) &&
          lastBandwidthTest.timestamp &&
          Date.now() - lastBandwidthTest.timestamp < BandwidthTextExpiresInMs;
        if (skipBandwidthTest && !alwaysRun) {
          console.log('Skipping another bandwidth test');
          resolve(angular.extend({ fromCache: true }, lastBandwidthTest));
          return;
        }

        this._bandwidthTest = new BandwidthTest({
          overallTimeout: OverallTimeoutInSec,
          onProgress: onProgress,
        });
        const uploadTestUrl = `${BANDWIDTH_TEST_URL}/upload`;
        this._bandwidthTest
          .testUpload({ url: uploadTestUrl })
          .then((r) => {
            resolve({
              success: true,
              requesterAddress: Requester.address,
              availableBandwidth: r.bandwidth,
              rSquared: r.rSquared,
              timestamp: Date.now(),
            });
          })
          .catch((e) => {
            console.warn('Failed to get available bandwidth; using default.', e);
            resolve({
              success: false,
              requesterAddress: Requester.address,
              availableBandwidth: DefaultBandwidth,
              timestamp: Date.now(),
            });
          });
      });
    }).then((bw) => {
      if (bw.success) {
        storage.set('lastBwTest', JSON.stringify(bw));
      }
      return bw;
    });
  };

  function getBitrateLimits(height, frameRate) {
    // Attempt to match bitrate based on other parameters to line up closely to the
    // transcoded output https://github.com/boxcast/origin_task/blob/master/stream/variant.go
    // NOTE that RTMP doesn't support UHD resolutions so they're not accounted for here.
    var audioMin = 48000,
      audioMax = 192000,
      videoMin = 300000,
      videoMax = 6000000;
    if (height < 720) {
      videoMax = 1700000;
    } else if (height == 720 && frameRate == 30) {
      videoMax = 2750000;
    } else if (height == 720 && frameRate == 60) {
      videoMax = 2750000;
    } else if (height == 1080 && frameRate == 30) {
      videoMax = 6000000;
    } else if (height == 1080 && frameRate == 60) {
      videoMax = 8000000;
    }

    return {
      audio: { min: audioMin, max: audioMax },
      video: { min: videoMin, max: videoMax },
      inflectionPoint: audioMax + videoMin + (audioMax - audioMin) * videoToAudioBitrateScale,
    };
  }

  this.getBitrates = function (
    bandwidth,
    videoBitrate = null,
    audioBitrate = null,
    height = null,
    frameRate = null
  ) {
    const availableBandwidth = bandwidthFactor * bandwidth;
    const bitrateLimits = getBitrateLimits(height || 720, frameRate || 30);
    console.log('availableBandwidth', availableBandwidth);
    console.log('bitrateLimits', bitrateLimits);
    if (videoBitrate && !audioBitrate) {
      // Video bitrate fixed, choose audio
      audioBitrate = Math.min(
        Math.max(Math.round(availableBandwidth - videoBitrate), bitrateLimits.audio.min),
        bitrateLimits.audio.max
      );
    } else if (!videoBitrate && audioBitrate) {
      // Audio bitrate fixed, choose video
      videoBitrate = Math.min(
        Math.max(Math.round(availableBandwidth - audioBitrate), bitrateLimits.video.min),
        bitrateLimits.video.max
      );
    } else if (!videoBitrate && !audioBitrate) {
      // Choose both bitrates
      const min = bitrateLimits.audio.min + bitrateLimits.video.min;
      if (availableBandwidth <= min) {
        audioBitrate = bitrateLimits.audio.min;
        videoBitrate = bitrateLimits.video.min;
      } else if (availableBandwidth < bitrateLimits.inflectionPoint) {
        const max = bitrateLimits.audio.max + bitrateLimits.video.max,
          dX = (availableBandwidth - min) / (max - min);
        audioBitrate = Math.round(
          bitrateLimits.audio.min + dX * (bitrateLimits.audio.max - bitrateLimits.audio.min)
        );
        videoBitrate = Math.round(
          bitrateLimits.video.min + dX * (bitrateLimits.video.max - bitrateLimits.video.min)
        );
      } else {
        audioBitrate = bitrateLimits.audio.max;
        var leftoverForVideo = Math.round(availableBandwidth - audioBitrate);
        videoBitrate = Math.max(
          Math.min(leftoverForVideo, bitrateLimits.video.max),
          bitrateLimits.video.min
        );
      }
    }

    if (!videoBitrate) {
      videoBitrate = 0; //could be NaN
    }

    // Scale to nearest supported value
    videoBitrate = floorToClosest(videoBitrate, videoBitrates);
    audioBitrate = floorToClosest(audioBitrate, audioBitrates);

    // Recommend between 75% and 150% of the stated transcode limit for the given
    // resolution/framerate
    const minVideoBitrate = 0.75 * bitrateLimits.video.max;
    const maxVideoBitrate = 1.5 * bitrateLimits.video.max;

    return {
      videoBitrate,
      audioBitrate,
      minVideoBitrate,
      maxVideoBitrate,
    };
  };

  function floorToClosest(val, vals) {
    for (var i = 0; i < vals.length; i++) {
      if (vals[i] > val) {
        return vals[Math.max(0, i - 1)];
      }
    }
    return vals[vals.length - 1];
  }
});
export default module;



// WEBPACK FOOTER //
// ./app/services/bandwidth-test-client.js